import { useState, useCallback, useEffect } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { plans as plansApi, tenants as tenantsApi } from '../api/client.js';
import { useApi } from '../hooks/useApi.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { EmptyState } from '../components/EmptyState.js';
import { useToast } from '../components/Toast.js';
import type { Plan } from '../types/index.js';

type DiscordRole = { id: string; name: string; color: string };

const EMOJI_OPTIONS = [
  { value: '', label: 'None' },
  { value: '⭐', label: '⭐' },
  { value: '💎', label: '💎' },
  { value: '🔥', label: '🔥' },
  { value: '👑', label: '👑' },
  { value: '🚀', label: '🚀' },
  { value: '⚡', label: '⚡' },
  { value: '🎮', label: '🎮' },
  { value: '🏆', label: '🏆' },
  { value: '💰', label: '💰' },
  { value: '🎯', label: '🎯' },
  { value: '🎁', label: '🎁' },
  { value: '💫', label: '💫' },
  { value: '🌟', label: '🌟' },
  { value: '🛡️', label: '🛡️' },
  { value: '🎵', label: '🎵' },
  { value: '🎨', label: '🎨' },
  { value: '💪', label: '💪' },
  { value: '🌈', label: '🌈' },
  { value: '✨', label: '✨' },
  { value: '🎭', label: '🎭' },
];

const PLAN_ICONS = ['violet', 'teal', 'amber', 'green'] as const;

export function PlansPage() {
  const { activeTenantId, activeTenant } = useTenant();
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);

  const fetcher = useCallback(
    () => (activeTenantId ? plansApi.list(activeTenantId) : Promise.resolve({ plans: [] as Plan[] })),
    [activeTenantId],
  );

  const { data, loading, error, refetch } = useApi(fetcher, [activeTenantId]);
  const planList = data?.plans.filter((p) => p.active) ?? [];

  const { data: rolesData } = useApi(
    activeTenantId ? () => tenantsApi.discordRoles(activeTenantId) : null,
    [activeTenantId],
  );
  const discordRoles = rolesData?.roles ?? [];

  const roleName = (rid: string) => {
    const found = discordRoles.find((r: DiscordRole) => r.id === rid);
    return found ? `@${found.name}` : rid;
  };

  const openCreate = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const openEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!activeTenantId || !deletingPlan) return;
    try {
      await plansApi.delete(activeTenantId, deletingPlan.id);
      console.log('[plans] deactivated:', deletingPlan.name);
      toast('Plan deactivated');
      await refetch();
    } catch (err) {
      console.error('[plans] delete failed:', err);
      toast('Failed to delete plan', 'error');
    } finally {
      setDeletingPlan(null);
    }
  };

  if (loading) return <LoadingSpinner text="Loading plans..." />;
  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Plans</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Configure your subscription tiers and pricing</p>
        </div>
        <button className="btn-sm btn-primary" onClick={openCreate}>+ New Plan</button>
      </div>

      {planList.length === 0 ? (
        <EmptyState
          title="No plans yet"
          description="Create your first subscription plan to start accepting payments."
          action={<button className="btn-sm btn-primary" onClick={openCreate}>+ New Plan</button>}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {planList.map((plan, i) => (
            <div key={plan.id} className="kpi" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className={`kpi-icon ${PLAN_ICONS[i % PLAN_ICONS.length]}`} style={{ width: 36, height: 36, borderRadius: 10 }}>
                    <span style={{ fontSize: 16 }}>{plan.emoji}</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: 'var(--fd)', fontSize: 16, fontWeight: 700 }}>{plan.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--t3)' }}>{plan.label}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn-sm btn-ghost" style={{ padding: '5px 12px', fontSize: 11 }} onClick={() => openEdit(plan)}>Edit</button>
                  <button className="btn-sm btn-ghost" style={{ padding: '5px 12px', fontSize: 11, color: 'var(--red)' }} onClick={() => setDeletingPlan(plan)}>Delete</button>
                </div>
              </div>
              <div style={{ fontFamily: 'var(--fd)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 4 }}>
                ${Number(plan.priceUsd).toFixed(2)}
                <span style={{ fontSize: 14, color: 'var(--t3)', fontWeight: 500 }}>/{plan.durationDays}d</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 14 }}>
                Role: {roleName(plan.roleId)}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="chain-badge chain-sol">SOL</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* renewal settings */}
      {activeTenant && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Renewal Settings</h3>
          <div className="kpi" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Auto-Renewal Reminders</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>
                  Send payment reminders {activeTenant.renewalReminderDays} days before expiry via DM
                </div>
              </div>
              <span className={`badge ${activeTenant.renewalEnabled ? 'badge-active' : 'badge-expired'}`}>
                {activeTenant.renewalEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div style={{ borderTop: '1px solid var(--b1)', marginTop: 14, paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Grace Period</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>Allow late payments within a window after expiry</div>
              </div>
              <span style={{ fontFamily: 'var(--fd)', fontSize: 14, fontWeight: 600 }}>
                {activeTenant.renewalGraceHours} hours
              </span>
            </div>
            <div style={{ borderTop: '1px solid var(--b1)', marginTop: 14, paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sweep Mode</div>
                <div style={{ fontSize: 12, color: 'var(--t3)' }}>How confirmed payments are moved to your wallet</div>
              </div>
              <span className="badge badge-active" style={{ textTransform: 'capitalize' }}>
                {activeTenant.sweepMode}-sweep
              </span>
            </div>
          </div>
        </div>
      )}

      {activeTenantId && (
        <PlanModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          plan={editingPlan}
          tenantId={activeTenantId}
          onSaved={() => void refetch()}
          existingPlans={planList}
          discordRoles={discordRoles}
          embedConfig={activeTenant?.embedConfig ?? null}
        />
      )}

      <ConfirmDialog
        open={deletingPlan !== null}
        title="Deactivate Plan"
        message={`Deactivate "${deletingPlan?.name}"? Existing subscribers won't be affected.`}
        confirmLabel="Deactivate"
        confirmColor="var(--red)"
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeletingPlan(null)}
      />
    </>
  );
}

// -- plan create/edit modal --

function PlanModal({
  open,
  onClose,
  plan,
  tenantId,
  onSaved,
  existingPlans,
  discordRoles,
  embedConfig,
}: {
  open: boolean;
  onClose: () => void;
  plan: Plan | null;
  tenantId: string;
  onSaved: () => void;
  existingPlans: Plan[];
  discordRoles: DiscordRole[];
  embedConfig: { title: string; description: string; color: number; footerText: string } | null;
}) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [emoji, setEmoji] = useState('⭐');
  const [priceUsd, setPriceUsd] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [roleId, setRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // sync form when plan changes or modal opens
  const isEdit = plan !== null;
  useEffect(() => {
    if (!open) return;
    if (plan) {
      setName(plan.name);
      setLabel(plan.label);
      setEmoji(plan.emoji);
      setPriceUsd(plan.priceUsd);
      setDurationDays(String(plan.durationDays));
      setRoleId(plan.roleId);
    } else {
      setName('');
      setLabel('');
      setEmoji('⭐');
      setPriceUsd('');
      setDurationDays('30');
      setRoleId('');
    }
    setError('');
  }, [plan, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !label || !priceUsd || !roleId) {
      setError('All fields are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isEdit && plan) {
        await plansApi.update(tenantId, plan.id, {
          name,
          label,
          emoji,
          priceUsd: Number(priceUsd),
          durationDays: Number(durationDays),
          roleId,
        });
        console.log('[plans] updated:', name);
      } else {
        await plansApi.create(tenantId, {
          name,
          label,
          emoji,
          priceUsd: Number(priceUsd),
          durationDays: Number(durationDays),
          roleId,
        });
        console.log('[plans] created:', name);
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error('[plans] save failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  // helper: resolve role ID to name
  const roleName = (rid: string) => {
    const found = discordRoles.find((r) => r.id === rid);
    return found ? `@${found.name}` : rid ? '@role' : '@role';
  };

  // build the full plan list for the embed preview
  // if editing, replace the edited plan; if creating, append the new one
  const previewPlans: { emoji: string; label: string; roleId: string; durationDays: number }[] = [];
  for (const p of existingPlans) {
    if (isEdit && plan && p.id === plan.id) {
      previewPlans.push({ emoji: emoji || '⭐', label: label || 'Plan Label', roleId, durationDays: Number(durationDays) || 30 });
    } else {
      previewPlans.push({ emoji: p.emoji, label: p.label, roleId: p.roleId, durationDays: p.durationDays });
    }
  }
  if (!isEdit) {
    previewPlans.push({ emoji: emoji || '⭐', label: label || 'New Plan', roleId, durationDays: Number(durationDays) || 30 });
  }

  const embedTitle = embedConfig?.title || 'Select Your Plan';
  const embedDesc = embedConfig?.description || 'Choose a subscription plan below to get started.';
  const embedColor = embedConfig?.color ?? 0x5865f2;
  const embedHex = `#${embedColor.toString(16).padStart(6, '0')}`;
  const embedFooter = embedConfig?.footerText || 'Payments processed via crypto';

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Plan' : 'New Plan'} width={820} closeOnBackdrop={false}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* form */}
        <form onSubmit={(e) => void handleSubmit(e)} className="modal-form" style={{ margin: 0 }}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pro" />
          </div>
          <div className="form-group">
            <label className="form-label">Label (shown in Discord)</label>
            <input className="form-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Pro — $29.99/30 days" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Emoji</label>
              <select className="form-input" value={emoji} onChange={(e) => setEmoji(e.target.value)} style={{ maxWidth: 90 }}>
                {EMOJI_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Price (USD)</label>
              <input className="form-input" type="number" step="0.01" min="0" value={priceUsd} onChange={(e) => setPriceUsd(e.target.value)} placeholder="29.99" />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Duration (days)</label>
              <input className="form-input" type="number" min="1" max="365" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Discord Role</label>
            {discordRoles.length > 0 ? (
              <select className="form-input" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                <option value="">Select a role...</option>
                {discordRoles.map((r: DiscordRole) => (
                  <option key={r.id} value={r.id}>@{r.name}</option>
                ))}
              </select>
            ) : (
              <input className="form-input" value={roleId} onChange={(e) => setRoleId(e.target.value)} placeholder="Role ID (connect bot to see roles)" style={{ fontFamily: 'monospace' }} />
            )}
          </div>
          {error && <div className="form-error">{error}</div>}
          <button type="submit" className="btn-sm btn-primary" style={{ width: '100%', padding: 12, fontSize: 14 }} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Plan' : 'Create Plan'}
          </button>
        </form>

        {/* live embed preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t3)' }}>Embed Preview</div>
          <div style={{ background: '#2b2d31', borderRadius: 8, overflow: 'hidden', borderLeft: `4px solid ${embedHex}` }}>
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f2f3f5', marginBottom: 8 }}>{embedTitle}</div>
              <div style={{ fontSize: 14, color: '#b5bac1', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{embedDesc}</div>
              {/* plan fields */}
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(previewPlans.length, 3)}, 1fr)`, gap: 4 }}>
                {previewPlans.map((p, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#f2f3f5' }}>
                      {p.emoji} {p.label}
                    </div>
                    <div style={{ fontSize: 12, color: '#b5bac1' }}>
                      {roleName(p.roleId)} for {p.durationDays} days
                    </div>
                  </div>
                ))}
              </div>
              {/* footer */}
              <div style={{ fontSize: 11, color: '#80848e', marginTop: 12, borderTop: '1px solid #3f4147', paddingTop: 8 }}>
                {embedFooter}
              </div>
            </div>
          </div>

          {/* dropdown preview */}
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t3)' }}>Dropdown Preview</div>
          <div style={{ background: '#1e1f22', borderRadius: 4, border: '1px solid #3f4147', overflow: 'hidden' }}>
            {previewPlans.map((p, i) => (
              <div key={i} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, borderTop: i > 0 ? '1px solid #3f4147' : 'none' }}>
                <span style={{ fontSize: 14 }}>{p.emoji}</span>
                <div>
                  <div style={{ fontSize: 14, color: '#f2f3f5' }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: '#b5bac1' }}>{p.durationDays}-day subscription</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
            Live preview of how the full embed looks in Discord when users run <code style={{ background: 'var(--s2)', padding: '1px 4px', borderRadius: 3 }}>/menu</code>
          </div>
        </div>
      </div>
    </Modal>
  );
}
